import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { formatSydneyTime } from '@/utils/timezone';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { Avatar } from '@/components/Avatar';
import { ConfirmModal } from '@/components/Modal';
import { ShareTicketModal } from '@/components/ticket-details/ShareTicketModal';
import {
  TicketStatus,
  TicketPriority,
  UserRole,
  getTicketStatusString,
  getTicketPriorityString
} from '@/types/api';
import type { Ticket } from '@/types/api';
import type { NavigationContext } from '@/types/ui';
import { ticketService } from '@/services/ticketService';
import { useAuth } from '@/contexts/AuthContext';

interface TicketHeaderProps {
  ticket: Ticket;
  onStatusChange: (status: TicketStatus) => void;
  onPriorityChange: (priority: TicketPriority) => void;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  canEdit: boolean;
  isMobile: boolean;
  className?: string;
  onTicketUpdate?: (updates: Partial<Ticket>) => void;
}

/**
 * Ticket Header Component
 * Full-width header with navigation, ticket info, and quick actions
 */
export const TicketHeader: React.FC<TicketHeaderProps> = ({
  ticket,
  onStatusChange,
  onPriorityChange,
  onNavigatePrevious,
  onNavigateNext,
  canEdit,
  isMobile,
  className,
  onTicketUpdate
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedSubject, setEditedSubject] = useState(ticket.subject);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [isUpdatingSubject, setIsUpdatingSubject] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeletingTicket, setIsDeletingTicket] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showResolveConfirmation, setShowResolveConfirmation] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);

  // Auth context for permission checking
  const auth = useAuth();
  
  // Copy ticket ID to clipboard
  const copyTicketId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(ticket.ticketNumber || ticket.id);
      setCopyFeedback('Copied!');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (err) {
      console.error('Failed to copy ticket ID:', err);
      setCopyFeedback('Failed to copy');
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  }, [ticket.id]);
  
  // Handle subject editing
  const handleSubjectEdit = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(true);
  }, [canEdit]);
  
  const handleSubjectSave = useCallback(async () => {
    if (editedSubject.trim() !== ticket.subject && editedSubject.trim() !== '') {
      setIsUpdatingSubject(true);
      try {
        const response = await ticketService.updateTicketSubject(ticket.id, editedSubject.trim());
        if (response.success && response.data && onTicketUpdate) {
          onTicketUpdate({ subject: editedSubject.trim() });
        } else {
          // Revert on failure
          setEditedSubject(ticket.subject);
        }
      } catch (error) {
        console.error('Failed to update subject:', error);
        setEditedSubject(ticket.subject);
      } finally {
        setIsUpdatingSubject(false);
      }
    }
    setIsEditing(false);
  }, [editedSubject, ticket.subject, ticket.id, onTicketUpdate]);
  
  const handleSubjectCancel = useCallback(() => {
    setEditedSubject(ticket.subject);
    setIsEditing(false);
  }, [ticket.subject]);
  
  // Handle keyboard events for subject editing
  const handleSubjectKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubjectSave();
    } else if (event.key === 'Escape') {
      handleSubjectCancel();
    }
  }, [handleSubjectSave, handleSubjectCancel]);
  
  // Navigate back to ticket list with preserved filters
  const navigateBack = useCallback(() => {
    const returnPath = location.state?.from || '/tickets';
    const returnState = location.state?.returnState || {};
    navigate(returnPath, { state: returnState });
  }, [navigate, location.state]);
  
  // Check if user has permission to delete tickets
  const canDeleteTicket = useMemo(() => {
    if (!auth.user) return false;
    // Only admins and agents can delete tickets
    return auth.isAdmin() || auth.isAgent();
  }, [auth]);
  
  // Handle delete ticket confirmation
  const handleDeleteTicket = useCallback(() => {
    if (!canDeleteTicket) {
      console.warn('User does not have permission to delete tickets');
      return;
    }
    
    setDeleteError(null);
    setShowDeleteConfirmation(true);
    setShowMoreActions(false);
  }, [canDeleteTicket]);
  
  // Confirm ticket deletion
  const confirmDeleteTicket = useCallback(async () => {
    if (!ticket.id) return;
    
    setIsDeletingTicket(true);
    setDeleteError(null);
    
    try {
      const response = await ticketService.deleteTicket(ticket.id);
      
      if (response.success) {
        // Show success message briefly before navigating
        setCopyFeedback('Ticket archived successfully');
        
        // Navigate back to ticket list after brief delay
        setTimeout(() => {
          navigateBack();
        }, 1000);
      } else {
        setDeleteError(response.message || 'Failed to delete ticket');
        setIsDeletingTicket(false);
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
      setDeleteError('An unexpected error occurred while deleting the ticket');
      setIsDeletingTicket(false);
    }
  }, [ticket.id, navigateBack]);
  
  // Cancel delete confirmation
  const cancelDeleteTicket = useCallback(() => {
    setShowDeleteConfirmation(false);
    setDeleteError(null);
  }, []);
  
  // Handle share ticket
  const handleShareTicket = useCallback(() => {
    setShowShareModal(true);
  }, []);
  
  // Handle share modal close
  const handleShareModalClose = useCallback(() => {
    setShowShareModal(false);
  }, []);
  
  // Handle successful share action
  const handleShareSuccess = useCallback((method: string) => {
    // Optional: Add analytics tracking or feedback
    console.log(`Ticket shared via ${method}`);
  }, []);

  // Check if user can modify ticket status (Admin/Agent roles only)
  const canModifyStatus = useMemo(() => {
    if (!auth.user) return false;
    return auth.isAdmin() || auth.isAgent();
  }, [auth]);

  // Handle resolve ticket
  const handleResolveTicket = useCallback(() => {
    if (!canModifyStatus || ticket.status === TicketStatus.Resolved) return;
    setStatusUpdateError(null);
    setShowResolveConfirmation(true);
    setShowMoreActions(false);
  }, [canModifyStatus, ticket.status]);

  // Handle close ticket
  const handleCloseTicket = useCallback(() => {
    if (!canModifyStatus || ticket.status === TicketStatus.Closed) return;
    setStatusUpdateError(null);
    setShowCloseConfirmation(true);
    setShowMoreActions(false);
  }, [canModifyStatus, ticket.status]);

  // Confirm resolve ticket
  const confirmResolveTicket = useCallback(async () => {
    if (!ticket.id) return;

    setIsUpdatingStatus(true);
    setStatusUpdateError(null);

    try {
      const response = await ticketService.updateTicketStatus(ticket.id, TicketStatus.Resolved);

      if (response.success && response.data) {
        // Load fresh ticket data from server to ensure UI reflects actual backend state
        const freshTicketResponse = await ticketService.getTicket(ticket.id);

        if (freshTicketResponse.success && freshTicketResponse.data && onTicketUpdate) {
          // Replace entire ticket with fresh server data
          onTicketUpdate(freshTicketResponse.data);
        } else {
          // Fallback to local state update if reload fails
          onStatusChange(TicketStatus.Resolved);
          if (onTicketUpdate) {
            onTicketUpdate({ status: TicketStatus.Resolved });
          }
        }

        setCopyFeedback('Ticket marked as resolved');
        setShowResolveConfirmation(false);
      } else {
        setStatusUpdateError(response.message || 'Failed to update ticket status');
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      setStatusUpdateError('An unexpected error occurred while updating the ticket status');
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [ticket.id, onStatusChange, onTicketUpdate]);

  // Confirm close ticket
  const confirmCloseTicket = useCallback(async () => {
    if (!ticket.id) return;

    setIsUpdatingStatus(true);
    setStatusUpdateError(null);

    try {
      const response = await ticketService.updateTicketStatus(ticket.id, TicketStatus.Closed);

      if (response.success && response.data) {
        // Load fresh ticket data from server to ensure UI reflects actual backend state
        const freshTicketResponse = await ticketService.getTicket(ticket.id);

        if (freshTicketResponse.success && freshTicketResponse.data && onTicketUpdate) {
          // Replace entire ticket with fresh server data
          onTicketUpdate(freshTicketResponse.data);
        } else {
          // Fallback to local state update if reload fails
          onStatusChange(TicketStatus.Closed);
          if (onTicketUpdate) {
            onTicketUpdate({ status: TicketStatus.Closed });
          }
        }

        setCopyFeedback('Ticket closed successfully. Redirecting to list...');
        setShowCloseConfirmation(false);

        // Redirect back to tickets list with a flash message
        const flash = {
          type: 'success' as const,
          message: `Ticket ${ticket.ticketNumber || ticket.id} closed successfully`,
        };
        setTimeout(() => {
          const returnPath = (location.state as any)?.from || '/tickets';
          const returnState = (location.state as any)?.returnState || {};
          navigate(returnPath, { state: { ...returnState, flash } });
        }, 1500);
      } else {
        setStatusUpdateError(response.message || 'Failed to update ticket status');
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      setStatusUpdateError('An unexpected error occurred while updating the ticket status');
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [ticket.id, onStatusChange, onTicketUpdate]);

  // Cancel status update confirmations
  const cancelResolveTicket = useCallback(() => {
    setShowResolveConfirmation(false);
    setStatusUpdateError(null);
  }, []);

  const cancelCloseTicket = useCallback(() => {
    setShowCloseConfirmation(false);
    setStatusUpdateError(null);
  }, []);
  
  // Status badge with color coding
  const getStatusBadge = (status: TicketStatus | number) => {
    // Convert numeric status to string enum value if needed
    let normalizedStatus: TicketStatus;
    if (typeof status === 'number') {
      switch (status) {
        case 0: normalizedStatus = TicketStatus.New; break;
        case 1: normalizedStatus = TicketStatus.Open; break;
        case 2: normalizedStatus = TicketStatus.Pending; break;
        case 3: normalizedStatus = TicketStatus.Resolved; break;
        case 4: normalizedStatus = TicketStatus.Closed; break;
        case 5: normalizedStatus = TicketStatus.OnHold; break;
        case 6: normalizedStatus = TicketStatus.Cancelled; break;
        default: normalizedStatus = TicketStatus.Open; break;
      }
    } else {
      normalizedStatus = status;
    }

    const statusConfig = {
      [TicketStatus.New]: { variant: 'secondary' as const, label: 'New' },
      [TicketStatus.Open]: { variant: 'info' as const, label: 'Open' },
      [TicketStatus.InProgress]: { variant: 'warning' as const, label: 'In Progress' },
      [TicketStatus.Pending]: { variant: 'warning' as const, label: 'Pending' },
      [TicketStatus.OnHold]: { variant: 'secondary' as const, label: 'On Hold' },
      [TicketStatus.Resolved]: { variant: 'success' as const, label: 'Resolved' },
      [TicketStatus.Closed]: { variant: 'secondary' as const, label: 'Closed' },
      [TicketStatus.Cancelled]: { variant: 'error' as const, label: 'Cancelled' }
    };

    const config = statusConfig[normalizedStatus] || { variant: 'secondary' as const, label: getTicketStatusString(normalizedStatus) };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };
  
  // Priority badge with color coding
  const getPriorityBadge = (priority: TicketPriority) => {
    const priorityConfig = {
      [TicketPriority.Low]: { variant: 'secondary' as const, label: 'Low' },
      [TicketPriority.Medium]: { variant: 'warning' as const, label: 'Medium' },
      [TicketPriority.High]: { variant: 'error' as const, label: 'High' },
      [TicketPriority.Urgent]: { variant: 'error' as const, label: 'Urgent' }
    };
    
    const config = priorityConfig[priority] || { variant: 'secondary' as const, label: getTicketPriorityString(priority) };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };
  
  // Format date for display
  const formatDate = (dateString: string) => formatSydneyTime(dateString);
  
  // Generate breadcrumb path based on navigation context
  const breadcrumbPath = useMemo(() => {
    const context = location.state?.context as NavigationContext | undefined;
    
    if (!context) {
      return 'All Tickets';
    }
    
    switch (context.type) {
      case 'search':
        return `Search Results: "${context.query || context.value}"`;
      
      case 'myTickets':
      case 'assignedTickets':
        return 'My Tickets';
      
      case 'unassignedTickets':
        return 'Unassigned Tickets';
      
      case 'companyFilter':
        return `Company: ${context.companyName || context.value}`;
      
      case 'statusFilter':
        const statusLabels = {
          [TicketStatus.New]: 'New',
          [TicketStatus.Open]: 'Open',
          [TicketStatus.InProgress]: 'In Progress',
          [TicketStatus.Pending]: 'Pending',
          [TicketStatus.OnHold]: 'On Hold',
          [TicketStatus.Resolved]: 'Resolved',
          [TicketStatus.Closed]: 'Closed',
          [TicketStatus.Cancelled]: 'Cancelled'
        };
        return `${statusLabels[context.value as TicketStatus] || context.value} Tickets`;
      
      case 'priorityFilter':
        const priorityLabels = {
          [TicketPriority.Low]: 'Low',
          [TicketPriority.Medium]: 'Medium',
          [TicketPriority.High]: 'High',
          [TicketPriority.Urgent]: 'Urgent'
        };
        return `${priorityLabels[context.value as TicketPriority] || context.value} Priority Tickets`;
      
      case 'tagFilter':
        return `Tagged: ${context.tagName || context.value}`;
      
      case 'dateFilter':
        return `${context.label || context.type} Tickets`;
      
      case 'savedView':
        return context.viewName || 'Saved View';
      
      case 'overdue':
        return 'Overdue Tickets';
      
      case 'vipCustomers':
        return 'VIP Customer Tickets';
      
      case 'customFilter':
        return context.label || 'Filtered Tickets';
      
      case 'newTicket':
        return 'New Ticket Created';
      
      default:
        return context.label || 'All Tickets';
    }
  }, [location.state]);
  
  return (
    <div className={clsx('bg-white border-b border-gray-200 shadow-sm', className)}>
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb and Navigation */}
        {/* BugHerd #148: both rows wrap rather than squeezing their contents. At 320px the
            breadcrumb was compressed to 84px, which broke the ticket number across four
            lines instead of letting the row reflow. */}
        <div className="flex flex-wrap items-center justify-between gap-2 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <button
              onClick={navigateBack}
              className="hover:text-gray-700 focus:outline-none focus:text-gray-700"
            >
              {breadcrumbPath}
            </button>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-gray-900 font-medium whitespace-nowrap">Ticket #{ticket.ticketNumber || ticket.id}</span>
          </div>
          
          {/* Ticket Navigation */}
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size={isMobile ? 'md' : 'sm'}
              onClick={onNavigatePrevious}
              title="Previous ticket (J)"
              className="px-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="ghost"
              size={isMobile ? 'md' : 'sm'}
              onClick={onNavigateNext}
              title="Next ticket (K)"
              className="px-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
          </div>
        </div>
        
        {/* Main Header Content */}
        <div className="py-4 md:py-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            {/* Left Section: Ticket Info */}
            <div className="flex-1 min-w-0">
              {/* Ticket ID and Copy Button */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="relative">
                  <button
                    onClick={copyTicketId}
                    className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded px-1 py-0.5"
                    title="Copy ticket ID"
                  >
                    <span className="font-mono font-medium whitespace-nowrap">#{ticket.ticketNumber || ticket.id}</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                  {copyFeedback && (
                    <div className="absolute left-0 -bottom-8 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg">
                      {copyFeedback}
                    </div>
                  )}
                </div>
                {getStatusBadge(ticket.status)}
                {getPriorityBadge(ticket.priority)}
              </div>
              
              {/* Subject - Editable */}
              <div className="mb-3">
                {isEditing ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      onKeyDown={handleSubjectKeyDown}
                      className="flex-1 text-xl md:text-2xl font-bold text-gray-900 bg-transparent border-b-2 border-primary-500 focus:outline-none"
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size={isMobile ? 'md' : 'sm'}
                      onClick={handleSubjectSave}
                      className="px-2"
                      title="Save subject"
                    >
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </Button>
                    <Button
                      variant="ghost"
                      size={isMobile ? 'md' : 'sm'}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSubjectCancel();
                      }}
                      onClick={handleSubjectCancel}
                      className="px-2"
                      title="Cancel edit"
                    >
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={handleSubjectEdit}
                    disabled={!canEdit}
                    className={clsx(
                      // BugHerd #148: this button is inline-block, so `truncate` - which sets
                      // white-space: nowrap - sized it to max-content (648px) and pushed the
                      // whole document to 680px. Every ancestor was already constrained, so
                      // the cap has to be on the button itself. Below md the subject wraps,
                      // which is what a mobile agent needs; md and up keeps the single-line
                      // ellipsis, now actually bounded by max-w-full.
                      'text-left text-xl md:text-2xl font-bold text-gray-900 leading-tight',
                      'max-w-full break-words md:truncate',
                      canEdit && 'hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded'
                    )}
                    title={canEdit ? 'Click to edit subject' : ''}
                  >
                    {ticket.subject}
                  </button>
                )}
              </div>
              
              {/* Metadata */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <span>Created by</span>
                  <Avatar
                    name={ticket.requester.fullName}
                    size="xs"
                    className="inline-flex"
                  />
                  <span className="font-medium text-gray-900">{ticket.requester.fullName}</span>
                </div>
                
                {ticket.company && (
                  <div className="flex items-center space-x-1">
                    <span>from</span>
                    <span className="font-medium text-gray-900">{ticket.company.name}</span>
                  </div>
                )}
                
                {ticket.assignedTo && (
                  <div className="flex items-center space-x-1">
                    <span>assigned to</span>
                    <Avatar
                      name={ticket.assignedTo.fullName}
                      size="xs"
                      className="inline-flex"
                    />
                    <span className="font-medium text-gray-900">{ticket.assignedTo.fullName}</span>
                  </div>
                )}
                
                <div className="flex items-center space-x-1">
                  <span>created</span>
                  <time 
                    dateTime={ticket.createdAt}
                    title={formatDate(ticket.createdAt)}
                    className="font-medium text-gray-900"
                  >
                    {formatDate(ticket.createdAt)}
                  </time>
                </div>
                
                {ticket.updatedAt !== ticket.createdAt && (
                  <div className="flex items-center space-x-1">
                    <span>updated</span>
                    <time 
                      dateTime={ticket.updatedAt}
                      title={formatDate(ticket.updatedAt)}
                      className="font-medium text-gray-900"
                    >
                      {formatDate(ticket.updatedAt)}
                    </time>
                  </div>
                )}
                
                {ticket.tags && Array.isArray(ticket.tags) && ticket.tags.length > 0 && (
                  <div className="flex items-center space-x-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M7 7h.01M7 3h5l6.71 6.71a2 2 0 010 2.83l-4.88 4.88a2 2 0 01-2.83 0L4.3 10.7A2 2 0 014.3 7.87L11 1.17A2 2 0 0112.17 1h4.95A2 2 0 0119 3v4.95" />
                    </svg>
                    <div className="flex flex-wrap gap-1">
                      {ticket.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Section: Quick Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {/* Share Button */}
              <Button
                variant="outline"
                size={isMobile ? 'md' : 'sm'}
                onClick={handleShareTicket}
                className="flex items-center space-x-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                <span>Share</span>
              </Button>

              {/* Resolve Button - Only show for Admin/Agent and if not already resolved */}
              {canModifyStatus && ticket.status !== TicketStatus.Resolved && ticket.status !== TicketStatus.Closed && (
                <Button
                  variant="outline"
                  size={isMobile ? 'md' : 'sm'}
                  onClick={handleResolveTicket}
                  className="flex items-center space-x-1 text-blue-600 border-blue-300 hover:bg-blue-50 hover:border-blue-400"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Mark as Resolved</span>
                </Button>
              )}

              {/* Close Button - Only show for Admin/Agent and if not already closed */}
              {canModifyStatus && ticket.status !== TicketStatus.Closed && (
                <Button
                  variant="outline"
                  size={isMobile ? 'md' : 'sm'}
                  onClick={handleCloseTicket}
                  className="flex items-center space-x-1 text-gray-600 border-gray-300 hover:bg-gray-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Close Ticket</span>
                </Button>
              )}
              
              {/* More Actions Dropdown */}
              <div className="relative">
                <Button
                  variant="outline"
                  size={isMobile ? 'md' : 'sm'}
                  onClick={() => setShowMoreActions(!showMoreActions)}
                  className="flex items-center space-x-1"
                >
                  <span>More</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
                
                {showMoreActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                      onClick={() => {
                        window.print();
                        setShowMoreActions(false);
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      <span>Print</span>
                    </button>
                    
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                      onClick={() => {
                        handleSubjectEdit();
                        setShowMoreActions(false);
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Edit Details</span>
                    </button>
                    
                    {canDeleteTicket && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center space-x-2"
                        onClick={handleDeleteTicket}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>Archive Ticket</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Share Ticket Modal */}
      <ShareTicketModal
        ticket={ticket}
        userRole={auth.user?.role || UserRole.EndUser}
        isOpen={showShareModal}
        onClose={handleShareModalClose}
        onShare={handleShareSuccess}
      />
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirmation}
        onClose={cancelDeleteTicket}
        onConfirm={confirmDeleteTicket}
        title="Archive Ticket"
        message={`Are you sure you want to archive ticket #${ticket.ticketNumber || ticket.id}?\n\nTicket: ${ticket.subject}\n\nThis action will archive the ticket, making it read-only. Archived tickets can still be viewed but cannot be modified. This action cannot be undone.`}
        confirmText="Archive Ticket"
        cancelText="Cancel"
        variant="warning"
        loading={isDeletingTicket}
      />

      {/* Resolve Confirmation Modal */}
      <ConfirmModal
        isOpen={showResolveConfirmation}
        onClose={cancelResolveTicket}
        onConfirm={confirmResolveTicket}
        title="Mark as Resolved"
        message={`Are you sure you want to mark ticket #${ticket.ticketNumber || ticket.id} as resolved?\n\nTicket: ${ticket.subject}\n\nThis will change the ticket status to 'Resolved' and notify the requester.`}
        confirmText="Mark as Resolved"
        cancelText="Cancel"
        variant="success"
        loading={isUpdatingStatus}
      />

      {/* Close Confirmation Modal */}
      <ConfirmModal
        isOpen={showCloseConfirmation}
        onClose={cancelCloseTicket}
        onConfirm={confirmCloseTicket}
        title="Close Ticket"
        message={`Are you sure you want to close ticket #${ticket.ticketNumber || ticket.id}?\n\nTicket: ${ticket.subject}\n\nThis will change the ticket status to 'Closed' and notify the requester.`}
        confirmText="Close Ticket"
        cancelText="Cancel"
        variant="secondary"
        loading={isUpdatingStatus}
      />
      
      {/* Delete Error Toast */}
      {deleteError && (
        <div className="fixed top-4 right-4 z-50 max-w-sm bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg animate-in slide-in-from-right duration-300">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-red-800">Archive Failed</h3>
              <p className="text-sm text-red-700 mt-1 break-words">{deleteError}</p>
            </div>
            <button
              onClick={() => setDeleteError(null)}
              className="flex-shrink-0 text-red-400 hover:text-red-500 transition-colors"
              aria-label="Dismiss archive error"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Status Update Error Toast */}
      {statusUpdateError && (
        <div className="fixed top-4 right-4 z-50 max-w-sm bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg animate-in slide-in-from-right duration-300">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-red-800">Status Update Failed</h3>
              <p className="text-sm text-red-700 mt-1 break-words">{statusUpdateError}</p>
            </div>
            <button
              onClick={() => setStatusUpdateError(null)}
              className="flex-shrink-0 text-red-400 hover:text-red-500 transition-colors"
              aria-label="Dismiss status update error"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
